import { useEffect, useState } from "react";
import type { MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { demoApi, problemMessage } from "@/api/client";
import type { GrainInventoryCropTotal, GrainInventoryElevator, GrainInventoryReport, GrainInventoryRow } from "@/api/types";
import "./ReportsGrainInventory.css";

type ReportStatus = "loading" | "ready" | "error";

/** ef-063-1c-c — a quantity in the split lists: thousands separators, at most two decimals, `0` for zero. */
function formatQty(qty: number): string {
  return qty.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

/** ef-063-1c-c — the "Where" cell: an elevator row reads `At {vendor}`, anything else `On the farm`. */
function whereLabel(row: GrainInventoryRow): string {
  return row.locationKind === "elevator" && row.vendorName ? `At ${row.vendorName}` : "On the farm";
}

function CropTotalList({ id, title, totals }: { id: string; title: string; totals: GrainInventoryCropTotal[] }) {
  return (
    <div className="grain-inventory__bucket">
      <h4 id={id} className="grain-inventory__bucket-title">{title}</h4>
      {totals.length === 0 ? (
        <p className="grain-inventory__none">None</p>
      ) : (
        <ul aria-labelledby={id} className="grain-inventory__list">
          {totals.map((t) => (
            <li key={t.crop}>
              {t.crop}: <span className="grain-inventory__num">{formatQty(t.qty)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Every active elevator bin, an empty one as `0` rather than missing (the rows omit a net-zero bin). */
function ElevatorList({ elevators }: { elevators: GrainInventoryElevator[] }) {
  return (
    <div className="grain-inventory__elevators">
      <h3 id="grain-inventory-elevators" className="grain-inventory__export-title">Elevators</h3>
      <ul aria-labelledby="grain-inventory-elevators" className="grain-inventory__list">
        {elevators.map((e) => (
          <li key={e.binId} data-bin-id={e.binId}>
            {e.binName} · {e.vendorName}: <span className="grain-inventory__num">{formatQty(e.onHand)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Grain inventory by bin — Reports' CURRENT-snapshot roster: on-hand quantity per
 * (bin, crop). ef-053-2, ADR 0009 §6 ("Reports is one picker" — a report renders inside
 * the template at `/reports/{slug}`, no area tab strip of its own; "All reports" below
 * is the only way back).
 *
 * <p>
 * D4 — NO DATE RANGE, NO APPLY BUTTON. Same current-snapshot shape
 * `ReportsLivestockInventory.tsx`'s own D4 note gives — there is no window to choose, so
 * this fetches on mount instead of waiting for a submit.
 * </p>
 *
 * <p>
 * NO ENTITY PICKER — same "no fake buttons" call `ReportsLivestockInventory.tsx`'s own
 * remarks make: `GrainInventoryReportEndpoints` resolves the tenant's own
 * single/oldest entity when `entityId` is omitted, which is every farm this booth demo
 * models.
 * </p>
 */
export function ReportsGrainInventory() {
  const [report, setReport] = useState<GrainInventoryReport | null>(null);
  const [status, setStatus] = useState<ReportStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function load(): Promise<void> {
      setStatus("loading");
      setError(null);
      try {
        const result = await demoApi.getGrainInventoryReport(undefined, controller.signal);
        if (cancelled) return;
        setReport(result);
        setStatus("ready");
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        setReport(null);
        setStatus("error");
        setError(problemMessage(err, "Could not load the grain inventory report."));
      }
    }

    void load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  /**
   * The export is its own request, fired on the click — not fetched alongside the report,
   * same reasoning `ReportsLivestockInventory.tsx`'s own identical download gives.
   */
  const [csvState, setCsvState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "done"; rows: number } | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function downloadCsv(): Promise<void> {
    setCsvState({ kind: "busy" });
    try {
      const csv = await demoApi.getGrainInventoryCsv();

      // The bytes are the server writer's output verbatim — same "add a blob and a
      // filename and nothing else" discipline ReportsLivestockInventory.tsx's identical
      // download follows.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "easyfarm-grain-inventory.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const rows = Math.max(csv.trim().split("\n").length - 1, 0);
      setCsvState({ kind: "done", rows });
    } catch (err) {
      setCsvState({
        kind: "error",
        message: problemMessage(err, "Could not build the grain inventory file."),
      });
    }
  }

  function handleAllReportsClick(event: MouseEvent<HTMLAnchorElement>): void {
    event.preventDefault();
    // Self-contained, same device AreaTabs.tsx's own handleNavClick already uses rather
    // than threading a `navigate` prop through every screen this job touches: pushState
    // doesn't fire popstate on its own, so App.tsx's existing listener is told directly.
    window.history.pushState({}, "", "/reports");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  const rows = report?.rows ?? [];
  const isEmpty = status === "ready" && rows.length === 0;
  // ef-063-1c-c — `locationKind`, `totals` and `elevators` are optional on the wire type: a response written
  // before 0089 (the legacy rows-only stub) renders exactly as it always did — one table, no split, no elevators.
  const hasWhere = rows.some((row) => row.locationKind !== undefined);
  const totals = report?.totals;
  const elevators = report?.elevators ?? [];

  return (
    <main className="grain-inventory">
      <PageHeader
        title="Grain inventory by bin"
        subtitle="Current on-hand quantity by bin and crop, as of right now"
      />

      <a href="/reports" className="grain-inventory__all-reports" onClick={handleAllReportsClick}>
        ← All reports
      </a>

      <section
        className="grain-inventory__panel"
        aria-labelledby="grain-inventory-heading"
        data-state={isEmpty ? "empty" : status}
      >
        <h2 id="grain-inventory-heading" className="grain-inventory__heading">
          Current snapshot
        </h2>

        {status === "loading" && (
          <StateMessage tone="loading" title="Loading the grain inventory report…" />
        )}

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the grain inventory report."
            detail={error ?? undefined}
          />
        )}

        {isEmpty && (
          <StateMessage
            tone="empty"
            title="No grain on hand."
            detail="Bins and movements you record will show up here."
          />
        )}

        {isEmpty && elevators.length > 0 && <ElevatorList elevators={elevators} />}

        {status === "ready" && rows.length > 0 && (
          <>
            <div
              className="grain-inventory__table-wrap"
              tabIndex={0}
              role="group"
              aria-labelledby="grain-inventory-heading"
            >
              <table className="grain-inventory__table ruled-rows">
                <caption className="grain-inventory__caption">
                  {rows.length} {rows.length === 1 ? "line" : "lines"}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Bin</th>
                    <th scope="col">Crop</th>
                    {hasWhere && <th scope="col">Where</th>}
                    <th scope="col" className="grain-inventory__num">On Hand</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={`${row.binId}-${row.crop}`} data-location={row.locationKind}>
                      <th scope="row" className="grain-inventory__bin">{row.binName}</th>
                      <td>{row.crop}</td>
                      {hasWhere && <td>{whereLabel(row)}</td>}
                      <td className="grain-inventory__num">{row.onHand}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totals && (
              <section
                className="grain-inventory__split"
                aria-labelledby="grain-inventory-split-heading"
                data-state="ready"
              >
                <h3 id="grain-inventory-split-heading" className="grain-inventory__export-title">
                  On hand by location
                </h3>
                <div className="grain-inventory__buckets">
                  <CropTotalList id="grain-inventory-on-farm" title="On the farm" totals={totals.onFarm} />
                  <CropTotalList id="grain-inventory-at-elevators" title="At elevators" totals={totals.atElevator} />
                  <CropTotalList id="grain-inventory-all" title="All grain" totals={totals.all} />
                </div>
              </section>
            )}

            {elevators.length > 0 && <ElevatorList elevators={elevators} />}

            <div className="grain-inventory__export">
              <h3 className="grain-inventory__export-title">Grain inventory for your records</h3>
              <p className="grain-inventory__export-copy">
                The same rows above, as a CSV a spreadsheet can open.
              </p>
              <button
                type="button"
                className="btn btn--secondary grain-inventory__export-btn"
                disabled={csvState.kind === "busy"}
                onClick={() => void downloadCsv()}
              >
                {csvState.kind === "busy" ? "Building the file…" : "Download CSV"}
              </button>

              {csvState.kind === "done" && (
                <p className="grain-inventory__export-result" role="status">
                  {csvState.rows} {csvState.rows === 1 ? "line" : "lines"} saved.
                </p>
              )}

              {csvState.kind === "error" && (
                <p className="grain-inventory__export-error" role="alert">
                  {csvState.message} Nothing was downloaded.
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
